import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, CircularProgress, Alert, Chip, TablePagination,
} from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import { amenityApi } from '../../services/api';
import { Amenity } from '../../types';

const AmenityList: React.FC = () => {
  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Amenity | null>(null);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [capacity, setCapacity] = useState('');
  const [baseFee, setBaseFee] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [baseDurationHours, setBaseDurationHours] = useState('');
  const [formError, setFormError] = useState('');

  const fetchAmenities = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await amenityApi.getAll();
      setAmenities(Array.isArray(res.data) ? res.data : res.data?.data || []);
    } catch (err: any) {
      setError('Failed to load amenities.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAmenities(); }, [fetchAmenities]);

  const openAddDialog = () => {
    setEditingItem(null);
    setName(''); setDesc(''); setCapacity('');
    setBaseFee(''); setHourlyRate(''); setBaseDurationHours('');
    setFormError('');
    setDialogOpen(true);
  };

  const openEditDialog = (a: Amenity) => {
    setEditingItem(a);
    setName(a.name); setDesc(a.description || ''); setCapacity(String(a.capacity));
    setBaseFee(a.baseFee ? String(a.baseFee) : '');
    setHourlyRate(a.hourlyRate ? String(a.hourlyRate) : '');
    setBaseDurationHours(a.baseDurationHours ? String(a.baseDurationHours) : '');
    setFormError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!name.trim()) { setFormError('Name is required.'); return; }
    setSaving(true); setFormError('');
    try {
      const payload = {
        name: name.trim(), description: desc.trim(), capacity: Number(capacity) || 0,
        baseFee: Number(baseFee) || 0,
        hourlyRate: Number(hourlyRate) || 0,
        baseDurationHours: Number(baseDurationHours) || 0,
      };
      if (editingItem) {
        await amenityApi.update(editingItem.id, payload);
      } else {
        await amenityApi.create(payload);
      }
      await fetchAmenities();
      setDialogOpen(false);
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Failed to save amenity.');
    } finally { setSaving(false); }
  };

  const formatRm = (v: number) => v > 0 ? `RM${v.toFixed(2)}` : '-';
  const formatPricing = (a: Amenity) => {
    if (!a.baseFee || a.baseFee === 0) return 'Free';
    if (!a.baseDurationHours || a.baseDurationHours === 0) return `RM${a.baseFee.toFixed(2)} (flat)`;
    return `RM${a.baseFee.toFixed(2)} / ${a.baseDurationHours}h + RM${a.hourlyRate.toFixed(2)}/hr extra`;
  };

  const displayed = amenities.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Amenities</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openAddDialog}>Add Amenity</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Capacity</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Pricing</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayed.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><Typography color="text.secondary">No amenities configured.</Typography></TableCell></TableRow>
              ) : displayed.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell><Typography sx={{ fontWeight: 600 }}>{a.name}</Typography></TableCell>
                  <TableCell>{a.description || '-'}</TableCell>
                  <TableCell>{a.capacity}</TableCell>
                  <TableCell>{formatPricing(a)}</TableCell>
                  <TableCell><Chip label={a.isActive ? 'Active' : 'Inactive'} color={a.isActive ? 'success' : 'default'} size="small" /></TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => openEditDialog(a)} color="primary"><Edit /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination rowsPerPageOptions={[5, 10, 25]} component="div" count={amenities.length} rowsPerPage={rowsPerPage} page={page} onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
        </TableContainer>
      )}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Amenity' : 'Add Amenity'}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{formError}</Alert>}
          <TextField label="Name" fullWidth margin="normal" value={name} onChange={e => setName(e.target.value)} required disabled={saving} />
          <TextField label="Description" fullWidth margin="normal" value={desc} onChange={e => setDesc(e.target.value)} multiline rows={2} disabled={saving} />
          <TextField label="Capacity" fullWidth margin="normal" value={capacity} onChange={e => setCapacity(e.target.value)} type="number" disabled={saving} />
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>Pricing (leave 0 for free amenities)</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Base Fee (RM)" size="small" sx={{ flex: 1 }} value={baseFee} onChange={e => setBaseFee(e.target.value)} type="number" disabled={saving} />
            <TextField label="Hourly Rate (RM)" size="small" sx={{ flex: 1 }} value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} type="number" disabled={saving} />
            <TextField label="Base Duration (hrs)" size="small" sx={{ flex: 1 }} value={baseDurationHours} onChange={e => setBaseDurationHours(e.target.value)} type="number" disabled={saving} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving} startIcon={saving ? <CircularProgress size={18} /> : undefined}>{saving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AmenityList;
